@if (loc.pickerOpen()) {
  <div class="lm-backdrop" (click)="close()"></div>
  <div class="lm" role="dialog" aria-modal="true" [attr.aria-label]="i18n.t('location.ariaDialog')">
    <div class="lm__head">
      <h2 class="lm__title">{{ i18n.t('location.title') }}</h2>
      <button type="button" class="lm__x" (click)="close()" [attr.aria-label]="i18n.t('common.close')">×</button>
    </div>

    <div class="lm__body">
      <!--
        Why the picker opened by itself. Shown only when the drift watcher was what opened it, so a
        shopper who tapped the header pill never sees an explanation for something they did on purpose.
      -->
      @if (drift.driftMeters(); as moved) {
        <div class="lm__drift" role="status">
          <span class="lm__drift-ico" aria-hidden="true">📍</span>
          <span class="lm__drift-text">
            <strong>{{ i18n.t('location.driftTitle') }}</strong>
            {{ i18n.t('location.driftBody', {
                 away: driftLabel(moved),
                 label: loc.location()?.label || '' }) }}
          </span>
          <button type="button" class="lm__drift-x" (click)="drift.dismiss()"
                  [attr.aria-label]="i18n.t('location.driftKeepAria')">{{ i18n.t('location.driftKeep') }}</button>
        </div>
      }

      <div class="lm__search">
        <input
          type="text"
          class="lm__input"
          [placeholder]="i18n.t('location.searchPlaceholder')"
          [(ngModel)]="query"
          (keyup.enter)="search()"
        />
        <button type="button" class="lm__searchbtn" (click)="search()" [disabled]="searching()">
          {{ searching() ? '…' : i18n.t('common.search') }}
        </button>
      </div>

      <button type="button" class="lm__locate" (click)="locateMe()" [disabled]="locating()">
        <span aria-hidden="true">📍</span> {{ locating() ? i18n.t('location.locating') : i18n.t('location.locateMe') }}
      </button>

      <!--
        Progress for the two steps behind "Locate me", both of which stall on a phone indoors. The GPS
        fix has a real 10s deadline so its bar is determinate and honest; the address lookup has none,
        so it gets an indeterminate stripe instead of an invented countdown.
      -->
      @if (locating() || resolvingAddress()) {
        <div class="lm__progress" role="status" aria-live="polite">
          <!--
            The bike rides the progress bar. While LOCATING there is a real percentage, so it is handed
            straight through and the bike sits at that point instead of looping — the measured position
            is information worth keeping, not something to replace with decoration. While resolving the
            address there is no deadline, so progress is null and the bike loops.
          -->
          <app-bike-loader class="lm__progress-loader" variant="bar"
                           [progress]="locating() ? locateProgress() : null" />
          <span class="lm__progress-text">
            @if (locating()) {
              {{ i18n.t('location.findingPosition') }}
            } @else {
              {{ i18n.t('location.lookingUpAddress') }}
            }
          </span>
        </div>
      }

      @if (error()) { <p class="lm__error">{{ error() }}</p> }

      @if (canListAddresses) {
        <div class="lm__saved">
          <span class="lm__saved-label">{{ i18n.t('location.savedHeading', { used: addresses().length, max: maxAddresses }) }}</span>
          @if (addressesLoading()) {
            <!-- The bare `bar` variant, no caption: this is a narrow strip inside a compact modal, and
                 the inline variant's padding and caption would push the map down the panel. -->
            <app-bike-loader variant="bar" class="lm__saved-loader" />
          } @else if (addressesFailed()) {
            <p class="lm__error">{{ i18n.t('location.savedLoadFailed') }}</p>
          } @else if (addresses().length) {
            <div class="lm__addrs">
              @for (a of addresses(); track a.id) {
                <div class="lm__addr-row" [class.is-selected]="selectedAddressId() === a.id">
                  <button
                    type="button"
                    class="lm__addr"
                    [class.is-selected]="selectedAddressId() === a.id"
                    (click)="selectAddress(a)"
                  >
                    <span class="lm__addr-icon" aria-hidden="true">{{ addressIcon(a.label) }}</span>
                    <span class="lm__addr-text">
                      <strong>
                        {{ addressName(a) }}
                        @if (a.isDefault) { <span class="lm__addr-default">{{ i18n.t('location.default') }}</span> }
                      </strong>
                      @if (addressDetail(a)) { <small class="lm__addr-sub">{{ addressDetail(a) }}</small> }
                    </span>
                  </button>
                  <button
                    type="button"
                    class="lm__addr-del"
                    [disabled]="removingId() === a.id"
                    [attr.aria-label]="i18n.t('location.removeAria', { name: addressName(a) })"
                    (click)="removeAddress(a, $event)"
                  >
                    {{ removingId() === a.id ? '…' : '🗑' }}
                  </button>
                </div>
              }
            </div>
          } @else {
            <p class="lm__saved-note">{{ i18n.t('location.savedNone') }}</p>
          }
        </div>
      }

      <div class="lm__popular">
        <span class="lm__popular-label">{{ i18n.t('location.popular') }}</span>
        <div class="lm__cities">
          @for (c of loc.popular; track c.name) {
            <button type="button" class="lm__city" (click)="pickCity(c)">{{ c.name }}</button>
          }
        </div>
      </div>

      <div #pickMap class="lm__map"></div>
      @if (needsPin()) {
        <p class="lm__zone-warn">⚠️ {{ i18n.t('location.needsPinWarn') }}</p>
      } @else {
        <p class="lm__hint">{{ i18n.t('location.mapHint') }}</p>
      }

      @if (pickupOnly()) {
        <p class="lm__zone-warn">ℹ️ {{ i18n.t('location.zonePickupOnly', { store: zoneStoreName() }) }}</p>
      } @else if (storeHasNoDelivery()) {
        <!-- The store is set up for pickup only: its zones (and routes) don't make it deliver. -->
        <p class="lm__zone-warn">ℹ️ {{ i18n.t('location.pickupOnlyStore', { store: zoneStoreName() }) }}</p>
      } @else if (outsideDeliveryArea()) {
        <p class="lm__zone-warn">⚠️ {{ i18n.t('location.zoneOutside', { store: zoneStoreName() }) }}</p>
      } @else if (hasZoneOverlay() && hasRealPin()) {
        <!-- A pin in a zone the store reaches over a route says where it is delivered from. -->
        <p class="lm__zone-ok">✓ {{ routeOkText() || i18n.t('location.zoneOk', { store: zoneStoreName() }) }}</p>
      }

      @if (canSaveAddresses && pending() && !selectedAddressId()) {
        @if (canSaveMore) {
          <div class="lm__savebox">
            <span class="lm__savebox-label">{{ i18n.t('location.streetLabel') }}</span>
            <input
              type="text"
              class="lm__input lm__street"
              [placeholder]="i18n.t('location.streetPlaceholder')"
              [ngModel]="pendingStreet()"
              (ngModelChange)="pendingStreet.set($event)"
              autocomplete="street-address"
              maxlength="200"
            />
            <span class="lm__savebox-label">{{ i18n.t('location.detailsLabel') }} <small>{{ i18n.t('location.optional') }}</small></span>
            <input
              type="text"
              class="lm__input lm__street"
              [placeholder]="i18n.t('location.detailsPlaceholder')"
              [ngModel]="pendingStreet2()"
              (ngModelChange)="pendingStreet2.set($event)"
              autocomplete="address-line2"
              maxlength="200"
            />
            <span class="lm__savebox-label">{{ i18n.t('location.noteLabel') }} <small>{{ i18n.t('location.optional') }}</small></span>
            <textarea
              class="lm__input lm__note"
              rows="2"
              [placeholder]="i18n.t('location.notePlaceholder')"
              [ngModel]="pendingNote()"
              (ngModelChange)="pendingNote.set($event)"
            ></textarea>
            <span class="lm__savebox-label">{{ i18n.t('location.saveAsLabel') }}</span>
            <div class="lm__labels">
              @for (l of labelOptions; track l) {
                <button
                  type="button"
                  class="lm__label"
                  [class.is-active]="pendingLabel() === l"
                  [disabled]="isLabelUsed(l)"
                  (click)="pickLabel(l)"
                >
                  {{ l }}@if (isLabelUsed(l)) { <span class="lm__label-used">{{ i18n.t('location.labelSaved') }}</span> }
                </button>
              }
            </div>
            <button type="button" class="lm__save" (click)="saveCurrentPin()" [disabled]="savingAddress()">
              {{ savingAddress() ? i18n.t('common.saving') : '＋ ' + i18n.t('location.saveAddress') }}
            </button>
          </div>
        } @else {
          <p class="lm__saved-note">{{ i18n.t('location.savedMax', { max: maxAddresses }) }}</p>
        }
      }
    </div>

    <div class="lm__foot">
      @if (pending(); as p) {
        <span class="lm__pending" title="{{ p.label }}">📍 {{ p.label }}</span>
      } @else {
        <span class="lm__pending lm__pending--empty">{{ i18n.t('location.nothingSelected') }}</span>
      }
      <button type="button" class="lm__confirm" (click)="confirm()"
              [disabled]="!pending() || needsPin()">{{ i18n.t('location.confirm') }}</button>
    </div>
  </div>
}
